<template>
  <div class="body-wrapper bg-[#F5F6FA] rounded-xl relative">
    <!-- Overlay for Toaster -->
    <div
      v-if="toasterQueue.length > 0"
      class="toaster-overlay"
    ></div>
    <div :class="{ 'pointer-events-none': toasterQueue.length > 0 }" class="flex">
      <!-- Sidebar (StepsNav) -->
      <StepsNav />

      <div class="right-col-content w-full p-4 h-[calc(100vh-108px)]">
        <div class="bg-white rounded-xl right-content-wrapper overflow-y-auto">
          <!-- Steps Heading -->
          <StepsHeading 
            :module-number="currentModule.number" 
            :title="currentModule.title" 
          />
          
          <!-- Skipped Module Popup -->
          <div v-if="showSkippedModulePopup" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div class="bg-white rounded-xl p-6 max-w-md w-full mx-4">
              <h3 class="text-[#00423D] text-xl font-semibold mb-4">Module Skipped</h3>
              <p class="text-[#414141] mb-6">This module has been skipped. Please go to the Module 9 Overview page to unskip it if you want to access this section.</p>
              <div class="flex justify-end">
                <button 
                  @click="handleGoToOverview" 
                  class="bg-[#00423D] text-white px-6 py-2 rounded-lg hover:bg-[#003632] transition-colors"
                >
                  Go to Module Overview
                </button>
              </div>
            </div>
          </div>

          <transition name="fade">
            <router-view v-slot="{ Component }">
              <component 
                :is="Component" 
                ref="currentComponentRef"
                @next="handleNextClick"
                v-if="!isRouteSkipped || isOverviewRoute"
              />
            </router-view>
          </transition>
        </div>

        <!-- bottom cursor  -->
        <div class="flex items-center justify-between mt-5" v-if="!isRouteSkipped || isOverviewRoute">
          <ul class="steps flex items-center">
            <li
              v-for="(step, index) in currentSteps"
              :key="index"
              :class="[
                'h-[6px] w-[3rem] sm:w-[1rem] md:w-[2rem] rounded-full mr-[12px] cursor-pointer',
                currentStepIndex === index ? 'bg-[#00423D]' : 'bg-[#EBE8E1]'
              ]"
              @click="navigateToStep(step.path)"
            ></li>
          </ul>
          <div>
            <button
              @click="handleNextClick"
              :disabled="isNavigating"
              class="text-[#ffffff] font-semibold text-[1rem] leading-[1.5rem] bg-[#00423D] rounded-xl h-[48px] w-full px-6 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ isNavigating ? 'Loading...' : (isLastStep ? 'Next Module' : 'Next') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Stack Container: top-right, new toasts at bottom, all same width -->
    <div class="fixed z-50 flex flex-col items-end gap-4 max-h-[90vh] overflow-y-auto w-[392px] top-4 right-4 sm:right-[20px] sm:top-[94px]" style="max-width: 392px;">
      <transition-group name="toaster-fade">
        <div v-for="toaster in toasterQueue" :key="toaster.id" class="relative">
          <CustomToaster
            :title="toaster.props.title"
            :resourceName="toaster.props.resourceName"
            :resourceLink="toaster.props.resourceLink"
            :additionalText="toaster.props.additionalText"
            @close="closeToaster(toaster.id)"
            @resource-link-click="handleToasterLink(toaster.props.resourceName)"
          />
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, provide, onMounted, nextTick, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import StepsNav from "@/components/StepsNav.vue";
import StepsHeading from "@/components/StepsHeading.vue";
import SkippedModuleOverlay from "@/components/SkippedModuleOverlay.vue";
import { useRoutesManagement } from '@/stores/routesManagement';
import { useSkippedModulesStore } from '@/stores/skippedModules';
import CustomToaster from '@/components/CustomToaster.vue';
import { useDevelopmentDocsStore } from '@/stores/developmentDocs';

const router = useRouter();
const route = useRoute();
const routesManagement = useRoutesManagement();
const skippedModulesStore = useSkippedModulesStore();
const devDocsStore = useDevelopmentDocsStore();

const currentModule = ref({ number: 1, title: "Getting Started" });

// Define steps for each module
const gettingStartedSteps = [
  { path: '/steps/getting-started/welcome', name: 'Welcome' },
  { path: '/steps/getting-started/teammember', name: 'Team Member' }
];

const aspirationalVisionSteps = [
  { path: '/steps/aspirational-vision/overview', name: 'Overview' },
  { path: '/steps/aspirational-vision/societal-challenge', name: 'Societal Challenge' },
  { path: '/steps/aspirational-vision/societal-impact', name: 'Societal / Community Impact' },
  { path: '/steps/aspirational-vision/mission-statement', name: 'Mission Statement' },
  { path: '/steps/aspirational-vision/aspirational-vision', name: 'Aspirational Vision' }
];

const modules = ref([
  {
    number: 1,
    title: "Getting Started",
    sections: [
      { title: "Welcome" },
      // { title: "Fundraising & Sustainability Self-Assessment" },
      { title: "Collaboration & Adding Team Members" },
      { title: "Fundraising Resources Overview" },
      { title: "Case for Support" },
      { title: "Stewardship Structure" },
    ],
  },
  {
    number: 2,
    title: "Strategic Planning for Fundraising Overview",
    sections: [
      { title: "Overview" }
    ],
  },
  {
    number: 3,
    title: "Vision & Mission",
    sections: [
      { title: "Overview" },
      { title: "Societal Challenge and Organizational Solution" },
      { title: "Societal / Community Impact" },
      { title: "Mission Statement" },
      { title: "Aspirational Vision" },
    ],
  },
  {
    number: 4,
    title: "Core Annual Programs and Budgets",
    sections: [
      { title: "Overview" },
      { title: "Naming Your Core Annual Programs" },
    ],
  },
  {
    number: 5,
    title: "Annual Operating Overhead Budget",
    sections: [
      { title: "Overview" },
      { title: "Annual Overhead Expenses" },
      { title: "Budget Justification" }
    ],
  },
  {
    number: 6,
    title: "Annual Revenue",
    sections: [
      { title: "Annual Revenue" }
    ],
  },
  {
    number: 7,
    title: "Fundraising Goal Overview",
    sections: [
      { title: "Year 1 Annual Operating Budget Summary" },
      { title: "Annual Fundraising Goals (3 Years)" }
    ],
  },
  {
    number: 8,
    title: "Evaluation: Outcomes and Outputs",
    sections: [
      { title: "Overview" },
    ],
  },
  {
    number: 9,
    title: "New Projects",
    sections: [
      { title: "Overview" },
      { title: "Enter New Projects" },
      // Dynamic sections will be added here for each new project
      // { title: "Project Name", projectId: "id", projectSlug: "slug", slug: "slug" }
      { title: "New Project(s) Fundraising Goal (3 Years)" }
    ],
  },
  {
    number: 10,
    title: "3-Year Fundraising Goal Overview",
    sections: [
      { title: "Summary" }
    ],
  },
  {
    number: 11,
    title: "Case for Support",
    sections: [
      { title: "Overview" },
      { title: "Letter From the Executive Director" },
      { title: "Our Story" },
      { title: "Call for More" },
      { title: "Annual Programs Summary" },
      { title: "Join Us" },
      { title: "Ways to Give" },
      { title: "Impact Statement" },
      { title: "Success Story Highlights" },
      { title: "Fundraising Talking Points" },
      { title: "Case for Support", path: "/case-statement-document" },
    ],
  },
  {
    number: 12,
    title: "Gift Pyramid",
    sections: [
      { title: "Overview" },
      { title: "Donor: Prospect Ratio & Gift Pyramid"}
    ],
  },
  {
    number: 13,
    title: "Stewardship Structure",
    sections: [
      { title: "Overview" },
      { title: "Donor Recognition and Benefit Structure" }
    ]
  },
  {
    number: 14,
    title: "Policies & Procedures Manual",
    sections: [
      { title: "Overview" },
      { title: "Policies and Procedures" },
      { title: "Development Documents" }
    ]
  },
  {
    number: 15,
    title: "Congratulations, Strategic Plan for Fundraising Completed",
    sections: [
      { title: "Certificate" }
    ]
  },
]);

// Determine which module steps to use based on the current route
const updateCurrentModule = (newPath) => {
    if (newPath.includes('/steps/aspirational-vision/')) {
        currentModule.value = { number: 3, title: "Vision & Mission" };
    } else if (newPath.includes('/steps/getting-started/')) {
        currentModule.value = { number: 1, title: "Getting Started" };
    } else if (newPath.includes('/steps/strategic-planning/')) {
        currentModule.value = { number: 2, title: "Strategic Planning for Fundraising Overview" };
    } else if (newPath.includes('/steps/funding-annual/')) {
        currentModule.value = { number: 4, title: "Core Annual Programs and Budgets" };
    } else if (newPath.includes('/steps/core-program-evaluation/')) {
        currentModule.value = { number: 8, title: "Evaluation: Outcomes and Outputs" };
    } else if (newPath.includes('/steps/finalizing-budget/')) {
        currentModule.value = { number: 5, title: "Annual Operating Overhead Budget" };
    } else if (newPath.includes('/steps/annual-revenue/')) {
        currentModule.value = { number: 6, title: "Annual Revenue" };
    } else if (newPath.includes('/steps/annual-operating-budget/')) {
        currentModule.value = { number: 7, title: "Fundraising Goal Overview" };
    } else if (newPath.includes('/steps/funding-new-projects/')) {
        currentModule.value = { number: 9, title: "New Projects" };
    } else if (newPath.includes('/steps/total-fundraising-goal/')) {
        currentModule.value = { number: 10, title: "3-Year Fundraising Goal Overview" };
    } else if (newPath.includes('/steps/finalize-your-case-for-support/')) {
        currentModule.value = { number: 11, title: "Case for Support" };
    } else if (newPath.includes('/steps/gift-pyramid/')) {
        currentModule.value = { number: 12, title: "Gift Pyramid" };
    } else if (newPath.includes('/steps/stewardship/')) {
        currentModule.value = { number: 13, title: "Stewardship Structure" };
    } else if (newPath.includes('/steps/fundraising-operations/')) {
        currentModule.value = { number: 14, title: "Policies & Procedures Manual" };
    } else if (newPath.includes('/steps/congratulations/')) {
        currentModule.value = { number: 15, title: "Congratulations, Strategic Plan for Fundraising Completed" };
    }
};

watch(
  () => route.path,
  (newPath) => {
    updateCurrentModule(newPath);
  },
  { immediate: true } // Ensure it runs on initial load
);

// Get current steps based on selected module
const currentSteps = computed(() => {
  switch (currentModule.value.number) {
    case 1:
      return [
        { path: '/steps/getting-started/welcome', name: 'Welcome' },
        { path: '/steps/getting-started/teammember', name: 'Team Member' },
        { path: '/steps/getting-started/fundraising-resources-overview', name: 'Fundraising Resources Overview' },
        { path: '/steps/getting-started/case-for-support', name: 'Case for Support' },
        { path: '/steps/getting-started/stewardship-structure', name: 'Stewardship Structure' }
      ];
    case 2:
      return [{ path: '/steps/strategic-planning/overview', name: 'Overview' }];
    case 3:
      return aspirationalVisionSteps;
    case 4:
      const module4 = modules.value.find(m => m.number === 4);
      if (!module4) return [];

      // Get core programs from localStorage
      const corePrograms = JSON.parse(localStorage.getItem('corePrograms') || '[]');
      
      // Create base sections
      const baseSections = [
        { path: '/steps/funding-annual/overview', name: 'Overview' },
        { path: '/steps/funding-annual/core-programs', name: 'Naming Your Core Annual Programs' }
      ];

      // Create dynamic program sections
      const dynamicSections = corePrograms.map(program => {
        const programSlug = program.program_name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
        return {
          path: `/steps/funding-annual/program/${programSlug}`,
          name: program.program_name,
          programId: program.id
        };
      });

      // Return only the base sections and dynamic sections
      return [...baseSections, ...dynamicSections];
    case 5:
      return [
        { path: '/steps/finalizing-budget/overview', name: 'Overview' },
        { path: '/steps/finalizing-budget/overhead-expenses', name: 'Annual Overhead Expenses' },
        { path: '/steps/finalizing-budget/budget-justification', name: 'Budget Justification' }
      ];
    case 6:
      return [
        { path: '/steps/annual-revenue/annual-revenue', name: 'Annual Revenue' }
      ];
    case 7:
      return [
        { path: '/steps/annual-operating-budget/summary', name: 'Year 1 Annual Operating Budget Summary' },
        { path: '/steps/annual-operating-budget/fundraising-goals', name: 'Annual Fundraising Goals (3 Years)' }
      ];
    case 8:
      const module8 = modules.value.find(m => m.number === 8);
      if (!module8) return [];
      const corePrograms8 = JSON.parse(localStorage.getItem('corePrograms') || '[]');
      // Base section
      const baseSections8 = [
        { path: '/steps/core-program-evaluation/overview', name: 'Overview' }
      ];
      // Dynamic program sections: each program has one step that contains both outcomes and outputs tabs
      const dynamicSections8 = corePrograms8.map(program => {
        const slug = program.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        return {
          path: `/steps/core-program-evaluation/program/${slug}`,
          name: program.program_name,
          programId: program.id
        };
      });
      return [...baseSections8, ...dynamicSections8];
    case 9:
      const module9 = modules.value.find(m => m.number === 9);
      if (!module9) return [];

      // Get new projects from localStorage
      const newProjects = JSON.parse(localStorage.getItem('newProjects') || '[]');
      
      // Create base sections for Module 9
      const module9BaseSections = [
        { path: '/steps/funding-new-projects/overview', name: 'Overview' },
        { path: '/steps/funding-new-projects/new-projects', name: 'Enter New Projects' }
      ];

      // Create dynamic project sections for Module 9
      const module9DynamicSections = newProjects.map(project => {
        const projectSlug = project.project_name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
        return {
          path: `/steps/funding-new-projects/project/${projectSlug}`,
          name: project.project_name,
          projectId: project.id
        };
      });

      // Create remaining sections for Module 9
      const module9RemainingSections = [
        { path: '/steps/funding-new-projects/new-project-fundraising-goals', name: 'New Project(s) Fundraising Goal (3 Years)' }
      ];

      // Return all sections in order
      return [...module9BaseSections, ...module9DynamicSections, ...module9RemainingSections];
    case 10:
      return [
        { path: '/steps/total-fundraising-goal/summary', name: 'Summary' }
      ];
    case 11:
      return [
        { path: '/steps/finalize-your-case-for-support/overview', name: 'Overview' },
        { path: '/steps/finalize-your-case-for-support/letter-from-executive-director', name: 'Letter From the Executive Director' },
        { path: '/steps/finalize-your-case-for-support/our-story', name: 'Our Story' },
        { path: '/steps/finalize-your-case-for-support/call-for-more', name: 'Call for More' },
        { path: '/steps/finalize-your-case-for-support/annual-programs-summary', name: 'Annual Programs Summary' },
        { path: '/steps/finalize-your-case-for-support/join-us', name: 'Join Us' },
        { path: '/steps/finalize-your-case-for-support/ways-to-give', name: 'Ways to Give' },
        { path: '/steps/finalize-your-case-for-support/impact-statement', name: 'Impact Statement' },
        { path: '/steps/finalize-your-case-for-support/success-story-highlights', name: 'Success Story Highlights' },
        { path: '/steps/finalize-your-case-for-support/fundraising-talking-points', name: 'Fundraising Talking Points' },
        { path: '/case-statement-document', name: 'Case for Support' }
      ];
    case 12:
      return [
        { path: '/steps/gift-pyramid/overview', name: 'Overview' },
        { path: '/steps/gift-pyramid/prospect-to-donor-ratio', name: 'Donor: Prospect Ratio & Gift Pyramid' }
      ];
    case 13:
      return [
        { path: '/steps/stewardship/overview', name: 'Overview' },
        { path: '/steps/stewardship/donor-recognition', name: 'Donor Recognition and Benefit Structure' }
      ];
    case 14:
      return [
        { path: '/steps/fundraising-operations/overview', name: 'Overview' },
        { path: '/steps/fundraising-operations/policies-procedures', name: 'Policies and Procedures' },
        { path: '/steps/fundraising-operations/development-documents', name: 'Development Documents' }
      ];
    case 15:
      return [
        { path: '/steps/congratulations/certificate', name: 'Certificate' }
      ];
    default:
      return [];
  }
});

const currentStepIndex = computed(() => {
  return currentSteps.value.findIndex(step => route.path === step.path);
});

const isLastStep = computed(() => {
  return currentStepIndex.value === currentSteps.value.length - 1;
});

const isPageValid = ref(true);

// Create triggerValidation function
const triggerValidation = () => {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      resolve(true);
    }, 1000);

    window.dispatchEvent(new CustomEvent('validate-step', {
      detail: {
        callback: (isValid) => {
          clearTimeout(timeout);
          isPageValid.value = isValid;
          resolve(isValid === undefined ? true : isValid);
        }
      }
    }));
  });
};

// Provide the validation state setter to child components
provide('setPageValidation', (isValid) => {
  isPageValid.value = isValid;
});

// Provide validation trigger to child components
provide('triggerValidation', triggerValidation);

// Add ref for the current component
const currentComponentRef = ref(null);

const showSkippedModulePopup = ref(false);
const isRouteSkipped = computed(() => {
  // Special case: Module 9 overview should not appear skipped even if module is skipped
  if (route.path === '/steps/funding-new-projects/overview') {
    return false;
  }
  
  // Explicitly check if the store is initialized before checking skipped status
  if (!skippedModulesStore.initialized || !Array.isArray(skippedModulesStore.skippedRoutes)) {
    return false;
  }
  
  return skippedModulesStore.isRouteSkipped(route.path);
});

const isOverviewRoute = computed(() => {
  return route.path.includes('/overview');
});

// Helper function to check if a route belongs to funding new projects module
const isFundingNewProjectsRoute = (path) => {
  return path.includes('/steps/funding-new-projects');
};

// Helper function to get the funding new projects overview route
const getFundingNewProjectsOverviewRoute = () => {
  return '/steps/funding-new-projects/overview';
};

// Helper function to get the total fundraising goal route (module 10)
const getTotalFundraisingGoalRoute = () => {
  return '/steps/total-fundraising-goal/summary';
};

const handleGoToOverview = () => {
  // Simply navigate to module 9's overview page
  router.push('/steps/funding-new-projects/overview');
  // Hide the popup
  showSkippedModulePopup.value = false;
};

const isRouteInSkippedModule = (path) => {
  // Make sure we have initialized valid skipped modules data
  if (!skippedModulesStore.initialized || !Array.isArray(skippedModulesStore.skippedRoutes)) {
    return false;
  }
  
  // Special case: Allow access to the overview page of module 9 even when skipped
  if (path === '/steps/funding-new-projects/overview') {
    return false;
  }
  
  // Use the store's isRouteSkipped method
  return skippedModulesStore.isRouteSkipped(path);
};

// Add a watch on route changes to reset tab state and show toaster
watch(
  () => route.path,
  (newPath) => {
    // Reset validation state
    isPageValid.value = true;
    
    // Reset tab state for the new component
    nextTick(() => {
      const currentComponent = currentComponentRef.value;
      if (currentComponent?.activeTab !== undefined) {
        currentComponent.activeTab = 0;
      }
    });

    // Show toaster if on total fundraising goal summary page
    if (newPath === '/steps/total-fundraising-goal/summary') {
      addToaster({
        title: 'Well done!',
        resourceName: 'Understanding Your Fundraising Goals',
        resourceLink: '#'
      });
    }
  }
);

// Toast stack logic: new toasts are added at the bottom (push), stack grows downward from top-right
const toasterQueue = ref([]);
const toasterId = ref(0);
let toasterTimeouts = new Map();

const addToaster = (props) => {
  const id = `toaster-${toasterId.value++}`;
  // Add new toast at the bottom of the stack
  toasterQueue.value.push({
    id,
    props: {
      title: props.title,
      resourceName: props.resourceName,
      resourceLink: props.resourceLink,
      additionalText: props.additionalText || ''
    }
  });
  // Set timeout for this specific toaster
  const timeout = setTimeout(() => {
    closeToaster(id);
  }, 10000); // 10 seconds
  toasterTimeouts.set(id, timeout);
  return id;
};

// Provide addToaster globally for child components
provide('addToaster', addToaster);

function closeToaster(id) {
  // Clear the timeout for this toaster
  const timeout = toasterTimeouts.get(id);
  if (timeout) {
    clearTimeout(timeout);
    toasterTimeouts.delete(id);
  }
  // Remove the toaster from the queue by ID
  const index = toasterQueue.value.findIndex(t => t.id === id);
  if (index !== -1) {
    toasterQueue.value.splice(index, 1);
  }
}

function handleToasterLink(resourceName) {
  // Find the toaster with this resourceName
  const toaster = toasterQueue.value.find(t => t.props.resourceName === resourceName);
  if (toaster) closeToaster(toaster.id);

  // Parse the resourceLink to extract query parameters
  const resourceLink = toaster?.props.resourceLink || '';
  
  // If resourceLink is '#' or empty, just navigate with highlight
  if (resourceLink === '#' || !resourceLink) {
    router.push({ path: '/fundraising-collateral', query: { highlight: resourceName } });
    return;
  }
  
  // Try to parse the URL for query parameters
  try {
    const url = new URL(resourceLink, window.location.origin);
    const query = {};
    
    // Extract query parameters
    if (url.searchParams.has('highlight')) {
      query.highlight = url.searchParams.get('highlight');
    }
    if (url.searchParams.has('tab')) {
      query.tab = url.searchParams.get('tab');
    }

    // Navigate to the fundraising collateral page with the appropriate query parameters
    router.push({ path: '/fundraising-collateral', query });
  } catch (error) {
    // If URL parsing fails, fall back to highlight navigation
    router.push({ path: '/fundraising-collateral', query: { highlight: resourceName } });
  }
}

// Mapping from development document names to Fundraising Collateral card titles
const devDocToCollateralMap = {
  "pledge form": "Pledge Form",
  "gift agreement for an outright gift": "Gift Agreement: Pledge",
  "gift agreement for naming an endowment": "Gift Agreement: Planned Gift",
  "donor acknowledgment letter": "Standard Tax Receipt and Acknowledgement Letter",
  "stock and wire transfer instructions": "Stock and Wire Transfer Instructions"
};

const isNavigating = ref(false);

const handleNextClick = async () => {
  // Prevent multiple rapid clicks
  if (isNavigating.value) {
    return;
  }
  
  isNavigating.value = true;
  
  try {
    const currentStep = currentSteps.value[currentStepIndex.value];
    if (!currentStep) {
      return;
    }

    const currentComponent = currentComponentRef.value;
    
    // Check if the current component has tabs
    if (currentComponent?.activeTab !== undefined && currentComponent?.tabs?.length > 0) {
      // Validate current tab before proceeding
      const isCurrentTabValid = await currentComponent.validateCurrentTab();
      if (!isCurrentTabValid) {
        return; // Stop if current tab is not valid
      }

      // If we're not on the last tab, move to the next tab
      if (currentComponent.activeTab < currentComponent.tabs.length - 1) {
        currentComponent.activeTab++;
        return;
      }

      // If we're on the last tab, validate all tabs before proceeding
      const isValid = await triggerValidation();
      if (!isValid) {
        return;
      }
    } else {
      // For components without tabs, use normal validation
      const isValid = await triggerValidation();
      if (!isValid) {
        return;
      }
    }

    // If we're here, either there are no tabs or all tabs are valid
    // Proceed with the existing route navigation logic

    // Save Annual Overhead Expenses if on that step
    if (
      currentStep.path === '/steps/finalizing-budget/overhead-expenses' &&
      currentComponent && typeof currentComponent.saveExpenses === 'function'
    ) {
      try {
        await currentComponent.saveExpenses();
      } catch (error) {
        console.error("Error saving Annual Overhead Expenses:", error);
        return;
      }
    }

    if (currentStep.path.includes('/program/') && currentComponent?.saveExpenses) {
      try {
        await currentComponent.saveExpenses();
      } catch (error) {
        return;
      }
    }

    const currentPath = currentStep.path;
    
    // Show toaster only when on the Fundraising Talking Points step and Next is clicked
    if (currentPath === '/steps/finalize-your-case-for-support/success-story-highlights') {
      addToaster({
        title: 'Well done!',
        resourceName: 'Talking Points',
        resourceLink: '/fundraising-collateral?highlight=Talking Points'
      });
    }

    // Complete the current route and unlock the next route in a single batch
    const updates = [
      { routePath: currentPath, status: 1 } // Complete current route
    ];

    // Update the policies and procedures section in handleNextClick
    if (currentPath === '/steps/fundraising-operations/policies-procedures') {
      // Show single toaster for Policies and Procedures
      addToaster({
        title: 'Well done!',
        resourceName: 'Policies and Procedures',
        resourceLink: '#',
        additionalText: 'and other resources of the Internal Operations section'
      });
    }

    // Show toasters for selected development documents
    if (currentPath === '/steps/fundraising-operations/development-documents') {
      // Show single toaster for Development Documents
      addToaster({
        title: 'Well done!',
        resourceName: 'Gift Documentation',
        resourceLink: '/fundraising-collateral?tab=Gift Documentation',
      });
      devDocsStore.clearSelectedDocs();
    }

    // Determine the next path based on current module and step
    let nextPath = null;

    // Special handling for Module 4 navigation
    if (currentModule.value.number === 4) {
      const corePrograms = JSON.parse(localStorage.getItem('corePrograms') || '[]');
      
      // If we're at the "Naming Your Core Annual Programs" section and there are core programs
      if (currentPath === '/steps/funding-annual/core-programs' && corePrograms.length > 0) {
        // Navigate to the first program section
        const firstProgram = corePrograms[0];
        const programSlug = firstProgram.program_name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
        nextPath = `/steps/funding-annual/program/${programSlug}`;
      } else if (currentPath.includes('/program/')) {
        // If we're in a program section, navigate to the next program or core program evaluation
        const currentProgramIndex = corePrograms.findIndex(program => {
          const programSlug = program.program_name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
          return currentPath === `/steps/funding-annual/program/${programSlug}`;
        });
        
        if (currentProgramIndex !== -1 && currentProgramIndex < corePrograms.length - 1) {
          // Navigate to next program
          const nextProgram = corePrograms[currentProgramIndex + 1];
          const programSlug = nextProgram.program_name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
          nextPath = `/steps/funding-annual/program/${programSlug}`;
        } else if (currentProgramIndex === corePrograms.length - 1) {
          // Navigate to finalizing budget overview (Module 5 overview)
          nextPath = '/steps/finalizing-budget/overview';
        }
      }
    }

    // Special handling for Module 8 dynamic program navigation (previously Module 8)
    if (currentModule.value.number === 8 && !nextPath) {
      const corePrograms = JSON.parse(localStorage.getItem('corePrograms') || '[]');
      if (currentStep.path.includes('/program/')) {
        const slug = currentStep.path.split('/')[4];
        const programIndex = corePrograms.findIndex(program => 
          program.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') === slug
        );
        
        if (programIndex !== -1) {
          // If we're not on the last program, go to the next program
          if (programIndex < corePrograms.length - 1) {
            const nextProgram = corePrograms[programIndex + 1];
            const nextSlug = nextProgram.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
            nextPath = `/steps/core-program-evaluation/program/${nextSlug}`;
          } else {
            // If we're on the last program, go to the next module
            nextPath = '/steps/funding-new-projects/overview';
          }
        }
      }
    }

    // Special handling for Module 9 dynamic project navigation
    if (currentModule.value.number === 9 && !nextPath) {
      const newProjects = JSON.parse(localStorage.getItem('newProjects') || '[]');
      
      // If we're at the "Enter New Projects" section and there are new projects
      if (currentPath === '/steps/funding-new-projects/new-projects' && newProjects.length > 0) {
        // Navigate to the first project section
        const firstProject = newProjects[0];
        const projectSlug = firstProject.project_name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
        nextPath = `/steps/funding-new-projects/project/${projectSlug}`;
      } else if (currentStep.path.includes('/project/')) {
        const slug = currentStep.path.split('/')[4];
        const projectIndex = newProjects.findIndex(project => 
          project.project_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') === slug
        );
        
        if (projectIndex !== -1) {
          // If we're not on the last project, go to the next project
          if (projectIndex < newProjects.length - 1) {
            const nextProject = newProjects[projectIndex + 1];
            const nextSlug = nextProject.project_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
            nextPath = `/steps/funding-new-projects/project/${nextSlug}`;
          } else {
            // If we're on the last project, go to the next module section (New Project Fundraising Goals)
            nextPath = '/steps/funding-new-projects/new-project-fundraising-goals';
          }
        }
      }
    }

    // Show toaster if on donor recognition page
    if (currentPath === '/steps/stewardship/donor-recognition') {
      addToaster({
        title: 'Well done!',
        resourceName: 'Stewardship Structure',
        resourceLink: '#'
      });
    }

    // Show toaster if on gift pyramid prospect-to-donor-ratio page
    if (currentPath === '/steps/gift-pyramid/prospect-to-donor-ratio') {
      addToaster({
        title: 'Well done!',
        resourceName: 'Campaign Gift Pyramid',
        resourceLink: '#'
      });
    }

    // If no special path was determined, use standard navigation
    if (!nextPath) {
      // Get the next step from currentSteps
      const nextIndex = currentStepIndex.value + 1;
      if (nextIndex < currentSteps.value.length) {
        nextPath = currentSteps.value[nextIndex].path;
      } else {
        // If we're at the last step of the current module, move to the next module
        const currentModuleIndex = modules.value.findIndex(m => m.number === currentModule.value.number);
        if (currentModuleIndex < modules.value.length - 1) {
          // Special handling for transition from module 3 to module 4
          if (currentModule.value.number === 3) {
            nextPath = '/steps/funding-annual/overview';
            // Update module and navigate
            currentModule.value = { number: 4, title: "Core Annual Programs and Budgets" };
          } else {
            // For other modules, use the standard path construction
            nextPath = getDefaultPathForModule(currentModule.value.number + 1);
          }
        }
      }
    }

    // Add next path to updates if it needs to be unlocked
    if (nextPath && routesManagement.routeState[nextPath]?.status === -1) {
      updates.push({ routePath: nextPath, status: 0 }); // Unlock next route
    }

    // Perform batch update
    if (updates.length > 0) {
      await routesManagement.batchUpdateRoutes(updates);
    }
    
    // Small delay to ensure state updates are processed
    await nextTick();
    
    // Navigate to the next path
    if (nextPath) {
      await router.push(nextPath);
    }
  } catch (error) {
    console.error("Error in handleNextClick:", error);
  } finally {
    isNavigating.value = false;
  }
};

const navigateToStep = (path) => {
  // Check if the step is in a skipped module and is not the overview of funding new projects
  const isStepSkipped = isRouteInSkippedModule(path);
  const isFundingOverview = path === getFundingNewProjectsOverviewRoute();
  
  if (isStepSkipped && !isFundingOverview) {
    showSkippedModulePopup.value = true;
    return;
  }

  // Special handling for Module 4 dynamic program sections
  if (currentModule.value.number === 4 && path.includes('/steps/funding-annual/program/')) {
    const module4 = modules.value.find(m => m.number === 4);
    if (module4) {
      const programSlug = path.split('/').pop();
      const programExists = module4.sections.some(section => 
        section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') === programSlug
      );
      
      if (!programExists) {
        return; // Don't navigate if the program doesn't exist
      }
    }
  }
  
  // Special handling for Module 9 dynamic project sections
  if (currentModule.value.number === 9 && path.includes('/steps/funding-new-projects/project/')) {
    const module9 = modules.value.find(m => m.number === 9);
    if (module9) {
      const projectSlug = path.split('/').pop();
      const projectExists = module9.sections.some(section => 
        section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') === projectSlug
      );
      
      if (!projectExists) {
        return; // Don't navigate if the project doesn't exist
      }
    }
  }
  
  // Allow navigation to all routes regardless of status
  router.push(path);
};

const handleModuleChange = (moduleNumber) => {
  // When module changes, navigate to the first step of that module
  const steps = getDefaultPathForModule(moduleNumber);
  currentModule.value.number = moduleNumber;
  router.push(steps);
};

const getDefaultPathForModule = (moduleNumber) => {
  switch (moduleNumber) {
    case 1:
      return '/steps/getting-started/welcome';
    case 2:
      return '/steps/strategic-planning/overview';
    case 3:
      return '/steps/aspirational-vision/overview';
    case 4:
      return '/steps/funding-annual/overview';
    case 5:
      return '/steps/finalizing-budget/overview';
    case 6:
      return '/steps/annual-revenue/annual-revenue';
    case 7:
      return '/steps/annual-operating-budget/summary';
    case 8:
      return '/steps/core-program-evaluation/overview';
    case 9:
      return '/steps/funding-new-projects/overview';
    case 10:
      return '/steps/total-fundraising-goal/summary';
    case 11:
      return '/steps/finalize-your-case-for-support/overview';
    case 12:
      return '/steps/gift-pyramid/overview';
    case 13:
      return '/steps/stewardship/overview';
    case 14:
      return '/steps/fundraising-operations/overview';
    case 15:
      return '/steps/congratulations/certificate';
    default:
      return '/steps/getting-started/welcome';
  }
};

// Watch for route changes to handle direct URL navigation
watch(
  () => route.path,
  (newPath) => {
    updateCurrentModule(newPath);
  },
  { immediate: true }
);

// Reset validation state when route changes
watch(route, () => {
  isPageValid.value = true;
}, { immediate: true });

// Update the route watcher to handle skipped module access
watch(route, async (newRoute) => {
  const isSkipped = isRouteInSkippedModule(newRoute.path);
  const isFundingOverview = newRoute.path === getFundingNewProjectsOverviewRoute();
  
  // Show popup if trying to access any skipped module route except module 9 overview
  if (isSkipped && !isFundingOverview) {
    showSkippedModulePopup.value = true;
  } else {
    showSkippedModulePopup.value = false;
  }
});

onMounted(async () => {
  try {    
    // Get current module path from route
    const currentPath = route.path;
    const pathParts = currentPath.split('/');
    let currentModulePath = '';
    
    if (pathParts.length >= 3) {
      currentModulePath = `/${pathParts[1]}/${pathParts[2]}`;
    }
    
    // Check for specific module unskip flags
    const unskippedModulesJson = localStorage.getItem('unskippedModules');
    const unskipTimestamp = localStorage.getItem('unskipTimestamp');
    
    // If we have specific modules that were unskipped and we're currently viewing one of them
    let isCurrentModuleUnskipped = false;
    
    if (unskippedModulesJson && unskipTimestamp && currentModulePath) {
      try {
        const unskippedModules = JSON.parse(unskippedModulesJson);
        const now = Date.now();
        const fiveMinutesInMs = 5 * 60 * 1000;
        
        // Only apply if unskip is recent
        if ((now - parseInt(unskipTimestamp)) < fiveMinutesInMs) {
          // Check if current module was specifically unskipped
          if (unskippedModules.includes(currentModulePath)) {
            isCurrentModuleUnskipped = true;
            
            // Ensure UI is updated for this specific module
            showSkippedModulePopup.value = false;
          }
        } else {
          // Clean up old data
          localStorage.removeItem('unskippedModules');
          localStorage.removeItem('unskipTimestamp');
        }
      } catch (e) {
        // Error handling
      }
    }
    
    // Normal initialization flow
    // Load route state and skipped modules data in parallel
    const loadModules = routesManagement.populateRouteState(modules.value, true);
    let loadSkippedModules;
    
    // Check if the store is already initialized
    if (!skippedModulesStore.initialized || !Array.isArray(skippedModulesStore.skippedRoutes)) {
      loadSkippedModules = skippedModulesStore.fetchSkippedModules(true); // Force fresh data
    } else {
      loadSkippedModules = Promise.resolve(skippedModulesStore.skippedRoutes);
    }
    
    await Promise.all([loadModules, loadSkippedModules]);
        
    // Check if skipped modules were successfully loaded
    if (!skippedModulesStore.initialized || !Array.isArray(skippedModulesStore.skippedRoutes)) {
      // Add a delay and retry
      setTimeout(async () => {
        const retryResult = await skippedModulesStore.fetchSkippedModules(true);
        
        // Update UI state after retry
        checkCurrentRouteSkipStatus();
        
        // Force component re-render
        await nextTick();
      }, 800);
    } else {
      // If current module was specifically unskipped, ensure it's not in skipped routes
      if (isCurrentModuleUnskipped && currentModulePath) {
        // Filter out this specific module from skipped routes if it's still there
        if (skippedModulesStore.skippedRoutes.includes(currentModulePath)) {
          skippedModulesStore.skippedRoutes = skippedModulesStore.skippedRoutes
            .filter(route => route !== currentModulePath);
        }
      }
      
      checkCurrentRouteSkipStatus();
    }
    
    // Add a listener for the custom module-unskipped event
    window.addEventListener('module-unskipped', (event) => {
      // Get the specific modules that were unskipped
      const unskippedModules = event.detail.unskippedModules || [];
      
      if (unskippedModules.length > 0 && currentModulePath) {
        // Check if current module was unskipped
        if (unskippedModules.includes(currentModulePath)) {
          // Ensure it's not in skipped routes
          skippedModulesStore.skippedRoutes = skippedModulesStore.skippedRoutes
            .filter(route => !unskippedModules.includes(route));
          
          // Update UI state
          showSkippedModulePopup.value = false;
          
          // Force-refresh the UI
          nextTick(() => {
            checkCurrentRouteSkipStatus();
          });
        }
      }
    });
    
    // Force a component re-render
    await nextTick();
  } catch (error) {
    // Error handling
  }
});

// Helper function to check if current route is in a skipped module
const checkCurrentRouteSkipStatus = () => {
  updateCurrentModule(route.path);

  // Check if current route is in a skipped module and not the funding new projects overview
  const isSkipped = isRouteInSkippedModule(route.path);
  const isFundingOverview = route.path === getFundingNewProjectsOverviewRoute();
  
  // Show popup if trying to access any skipped module route except module 9 overview
  if (isSkipped && !isFundingOverview) {
    showSkippedModulePopup.value = true;
  } else {
    showSkippedModulePopup.value = false;
  }
};

// Expose handleNextClick to child components
provide('handleNext', handleNextClick);

// Add watcher for skippedModulesStore.skippedRoutes to update UI when it changes
watch(
  () => skippedModulesStore.skippedRoutes,
  (newSkippedRoutes, oldSkippedRoutes) => {    
    // Detect when modules are unskipped (lengths decrease)
    const wasSkipped = oldSkippedRoutes?.length > 0;
    const isNowUnskipped = newSkippedRoutes?.length === 0;
    
    if (wasSkipped && isNowUnskipped) {
      // When modules are unskipped, ensure we update our UI accordingly
      nextTick(() => {
        showSkippedModulePopup.value = false;
        checkCurrentRouteSkipStatus();
      });
      return;
    }
    
    // Check if this is a meaningful change (not just an empty array initializing to a different empty array)
    const hadRoutes = Array.isArray(oldSkippedRoutes) && oldSkippedRoutes.length > 0;
    const hasNewRoutes = Array.isArray(newSkippedRoutes) && newSkippedRoutes.length > 0;
    
    // If we're getting real data for the first time, ensure we check all UI states
    if (!hadRoutes && hasNewRoutes) {
      nextTick(() => {
        checkCurrentRouteSkipStatus();
      });
    }
    
    // Check if current route is in a skipped module
    if (route.path) {
      const isSkipped = isRouteInSkippedModule(route.path);
      const isFundingOverview = route.path === getFundingNewProjectsOverviewRoute();
      
      // Update popup visibility
      if (isSkipped && !isFundingOverview) {
        showSkippedModulePopup.value = true;
      } else {
        showSkippedModulePopup.value = false;
      }
    }
  },
  { deep: true, immediate: true }
);

// Watch for core programs changes
watch(() => localStorage.getItem('corePrograms'), (newValue) => {
    if (newValue) {
        const corePrograms = JSON.parse(newValue);
        const module4 = modules.value.find(m => m.number === 4);
        if (module4) {
            // Keep only the first two base sections (Overview and Core Programs)
            const baseSections = module4.sections.slice(0, 2);
            // Add dynamic sections for each core program
            const dynamicSections = corePrograms.map(program => ({
                title: program.program_name,
                programId: program.id,
                slug: program.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
            }));
            module4.sections = [...baseSections, ...dynamicSections];
            
            // Force a re-render of currentSteps
            nextTick(() => {
                currentSteps.value = currentSteps.value;
            });
        }
    }
}, { immediate: true });

// Watch for new projects changes
watch(() => localStorage.getItem('newProjects'), (newValue) => {
    if (newValue) {
        const newProjects = JSON.parse(newValue);
        const module9 = modules.value.find(m => m.number === 9);
        if (module9) {
            // Build sections directly without duplication
            const baseSections = [
                { title: "Overview" },
                { title: "Enter New Projects" }
            ];
            // Add dynamic sections for each new project
            const dynamicSections = newProjects.map((project, index) => ({
                title: `${index + 1} - ${project.project_name}`,
                projectId: project.id,
                slug: project.project_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
            }));
            // Add the remaining section
            const remainingSections = [
                { title: "New Project(s) Fundraising Goal (3 Years)" }
            ];
            module9.sections = [...baseSections, ...dynamicSections, ...remainingSections];
            
            // Force a re-render of currentSteps
            nextTick(() => {
                currentSteps.value = currentSteps.value;
            });
        }
    }
}, { immediate: true });

onMounted(() => {
    // Add event listener for core programs updates
    window.addEventListener('core-programs-updated', (event) => {
        const module4 = modules.value.find(m => m.number === 4);
        if (module4) {
            // Keep only the first two base sections (Overview and Core Programs)
            const baseSections = module4.sections.slice(0, 2);
            // Add dynamic sections for each core program
            const dynamicSections = event.detail.programs.map(program => ({
                title: program.program_name,
                programId: program.id,
                slug: program.program_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
            }));
            module4.sections = [...baseSections, ...dynamicSections];
            
            // Force a re-render of currentSteps
            nextTick(() => {
                currentSteps.value = currentSteps.value;
            });
        }
    });
    
    // Add event listener for new projects updates
    window.addEventListener('new-projects-updated', (event) => {
        const module9 = modules.value.find(m => m.number === 9);
        if (module9) {
            // Build sections directly without duplication
            const baseSections = [
                { title: "Overview" },
                { title: "Enter New Projects" }
            ];
            // Add dynamic sections for each new project
            const dynamicSections = event.detail.projects.map((project, index) => ({
                title: `${index + 1} - ${project.project_name}`,
                projectId: project.id,
                slug: project.project_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
            }));
            // Add the remaining section
            const remainingSections = [
                { title: "New Project(s) Fundraising Goal (3 Years)" }
            ];
            module9.sections = [...baseSections, ...dynamicSections, ...remainingSections];
            
            // Force a re-render of currentSteps
            nextTick(() => {
                currentSteps.value = currentSteps.value;
            });
        }
    });
});

onUnmounted(() => {
    // Remove event listener when component is unmounted
    window.removeEventListener('core-programs-updated', () => {});
    window.removeEventListener('new-projects-updated', () => {});
});

const isFundraisingTalkingPoints = computed(() => {
  return route.path === '/steps/finalize-your-case-for-support/fundraising-talking-points';
});
</script>

<style>
.right-content-wrapper {
  height: calc(100vh - 205px);
}
/* Add transition styles for fading effect */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

/* Updated toaster transition styles */
.toaster-fade-enter-active,
.toaster-fade-leave-active {
  transition: all 0.3s ease;
}

.toaster-fade-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.toaster-fade-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.toaster-fade-move {
  transition: transform 0.3s ease;
}

.toaster-overlay {
  position: fixed;
  inset: 0;
  z-index: 10;
  background: #00000050;
}

</style>

